import React from 'react';
import { ThumbnailSize, ItemFeatures } from '../../types/viewComposition';
import { centsToDisplay } from '../../utils/price';
import { ItemThumbnail } from './ItemThumbnail';
import { ItemDetails } from './ItemDetails';
import { CheckboxOverlay } from './CheckboxOverlay';
import { QuantityControls } from './QuantityControls';
import { QuantityBadge } from './QuantityBadge';
import '../../styles/components/itemCard.css';

const CONDITION_LABELS: Record<string, string> = {
  new: 'New',
  like_new: 'Like New',
  good: 'Good',
  fair: 'Fair',
  poor: 'Poor',
};

// SavedItem type is defined in parent components (InventoryView, InventoryContent)
// We use a generic interface here to avoid type conflicts
export interface SavedItem {
  id: number;
  name: string;
  image_id?: number | null;
  thumbnail_url?: string | null;
  image_url?: string | null;
  categories?: Array<{ id: number; name?: string; key?: string; label?: string }>;
  location?: string | null;
  description?: string | null;
  is_checked?: boolean;
  checked_at?: string | null;
  is_grocery?: boolean;
  uit_id?: number | null;
  unit?: string | null;
  quantity?: number;
  my_role?: string;
  shared_with_count?: number;
  [key: string]: any; // Allow additional properties from parent
}

interface ItemCardProps {
  item: SavedItem;
  size: ThumbnailSize;
  features: ItemFeatures;
  isSelected: boolean;
  isSelectMode?: boolean;
  token?: string | null;
  showQuantityInName?: boolean; // For shop mode: show quantity next to name
  highlightZeroQuantity?: boolean; // Show orange color for zero quantity items
  hideMenu?: boolean; // Hide 3-dot menu (e.g., in Shop mode)
  useQuantityBadge?: boolean; // Use badge instead of +/- controls (for swipe mode)
  isFadingOut?: boolean; // True when item is fading out after being checked
  isGuestToken?: boolean; // For guest views: use guest API endpoints
  currentUserId?: number; // Current user ID for determining ownership
  isSharedCollection?: boolean; // True if viewing a shared collection
  isReadOnly?: boolean; // Disable name click to edit (e.g., viewer role or Shop mode)
  showLockIcon?: boolean; // Show lock icon (default: same as isReadOnly, but can be overridden)
  isInBindle?: boolean; // Item is in the active bindle (show circle indicator in checkbox)
  
  // Callbacks - use 'any' for item parameter to accept any SavedItem variant
  onItemClick?: (item: any) => void;
  onImageClick?: (item: any) => void;
  onMenuClick?: (item: any) => void;
  onCheckboxToggle?: (item: any, e: React.MouseEvent) => void;
  onQuantityChange?: (itemId: number, newQuantity: number) => void;
  onPhotoUpload?: (itemId: number, file: File) => Promise<void>;
  onOpenImageSelection?: (itemId: number) => void;
  onQuantityBadgeClick?: (item: any) => void; // Called when quantity badge is tapped
  
  // Optional overlays
  renderOverlay?: (item: any) => React.ReactNode;
}

export const ItemCard: React.FC<ItemCardProps> = ({
  item,
  size,
  features,
  isSelected,
  isSelectMode,
  token,
  showQuantityInName,
  highlightZeroQuantity,
  hideMenu,
  isFadingOut,
  isGuestToken,
  currentUserId,
  isSharedCollection,
  isReadOnly,
  showLockIcon,
  useQuantityBadge,
  onItemClick,
  onImageClick,
  onMenuClick,
  onCheckboxToggle,
  onQuantityChange,
  onPhotoUpload,
  onOpenImageSelection,
  onQuantityBadgeClick,
  renderOverlay,
  isInBindle,
}) => {
  const isListLayout = (size === 'small' || size === 'medium' || size === 'large') && features.showDetails;
  const shouldShowLock = showLockIcon !== undefined ? showLockIcon : isReadOnly;
  
  const handleCardClick = () => {
    if (isSelectMode && onItemClick) {
      onItemClick(item);
    }
  };

  const handleMenuClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onMenuClick) {
      onMenuClick(item);
    }
  };

  const handleImageClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card selection in select mode
    if (item.image_id && onImageClick) {
      onImageClick(item);
    }
  };

  // Render placeholder for items without images
  const renderPlaceholder = () => {
    // Import ItemNamePlaceholder or create inline placeholder
    // For now, simple placeholder
    return (
      <div 
        style={{
          width: '100%',
          height: '100%',
          background: 'linear-gradient(135deg, var(--color-bg-tertiary) 0%, var(--color-border) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: size === 'large' ? '3rem' : size === 'medium' ? '2rem' : '1.75rem',
          fontWeight: 600,
          color: 'var(--color-text-faint)',
        }}
        onClick={() => {
          if (onOpenImageSelection) {
            onOpenImageSelection(item.id);
          }
        }}
      >
        {item.name.substring(0, 2).toUpperCase()}
      </div>
    );
  };

  // List layout (horizontal)
  if (isListLayout) {
    const listSizeClass = size === 'large' ? 'item-card-list-large' : size === 'medium' ? 'item-card-list-medium' : '';
    const hasZeroQuantity = showQuantityInName && item.quantity === 0;
    const needsRightPadding = features.showCheckbox || (features.showQuantity && !useQuantityBadge);
    // Use vertical quantity controls on the right edge for stock collections
    
    return (
      <div
        className={`item-card-list ${listSizeClass} ${isSelected ? 'selected' : ''} ${isFadingOut ? 'fading-out' : ''}`}
        data-item-id={item.id}
        onClick={handleCardClick}
        style={{ cursor: isSelectMode ? 'pointer' : 'default' }}
      >
        {/* Thumbnail */}
        <ItemThumbnail
          item={item}
          size={size}
          token={token}
          onClick={handleImageClick}
          showCompletedBadge={false}
          renderPlaceholder={renderPlaceholder}
          isGuestToken={isGuestToken}
        />
        
        {/* Quantity badge on thumbnail (when using swipe mode, not marketplace) */}
        {features.showQuantity && useQuantityBadge && !features.showPrice && (
          <QuantityBadge
            quantity={item.quantity || 0}
            onClick={isSelectMode ? undefined : () => onQuantityBadgeClick?.(item)}
            highlightZero={highlightZeroQuantity}
            position="top-right"
          />
        )}
        
        {/* Details - name is clickable to open edit menu */}
        {/* Add small padding-right buffer when checkbox or vertical quantity controls are on the right */}
        <div style={{ flex: 1, paddingRight: needsRightPadding ? '8px' : '0' }}>
          <ItemDetails
            item={item}
            showCategories={features.showDetails}
            showLocation={features.showDetails}
            showPrice={features.showPrice}
            showShareBadge={features.showShareBadge}
            showQuantityInName={showQuantityInName}
            highlightZeroQuantity={highlightZeroQuantity}
            showSharedBy={features.showShareBadge && (size === 'small' || size === 'medium' || size === 'large')}
            currentUserId={currentUserId}
            isSharedCollection={isSharedCollection}
            onNameClick={isReadOnly ? undefined : () => onMenuClick?.(item)}
            onQuantityClick={features.showPrice && onQuantityBadgeClick ? () => onQuantityBadgeClick(item) : undefined}
          />
        </div>
        
        {/* Vertical quantity controls on the right edge for stock collections (only when not using badge and not in select mode) */}
        {features.showQuantity && !features.showPrice && !useQuantityBadge && !isSelectMode && (
          <QuantityControls
            itemId={item.id}
            quantity={item.quantity || 0}
            onChange={onQuantityChange}
            vertical={true}
            size={size === 'large' ? 'medium' : size === 'small' ? 'small' : 'medium'}
          />
        )}
        
        {/* Checkbox (if checklist) - positioned on right, top-aligned (hidden in select mode) */}
        {features.showCheckbox && !isSelectMode && (
          <CheckboxOverlay
            isChecked={item.is_checked || isFadingOut || false}
            onToggle={(e: React.MouseEvent) => onCheckboxToggle?.(item, e)}
            position="right"
            layout="list"
            size={size}
            isInBindle={isInBindle}
          />
        )}
        
        {/* Lock icon for read-only items */}
        {shouldShowLock && (
          <div
            style={{
              position: 'absolute',
              bottom: '8px',
              right: '8px',
              opacity: 0.5,
            }}
            aria-label="Read only"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
          </div>
        )}
        
        {/* Custom overlay */}
        {renderOverlay?.(item)}
      </div>
    );
  }

  // Grid layout (vertical card)
  const hasZeroQuantity = (showQuantityInName || highlightZeroQuantity) && item.quantity === 0;
  
  return (
    <div
      className={`item-card-grid item-card-${size} ${isSelected ? 'selected' : ''} ${item.is_checked ? 'completed' : ''} ${isFadingOut ? 'fading-out' : ''}`}
      data-item-id={item.id}
      onClick={handleCardClick}
      style={{ cursor: isSelectMode ? 'pointer' : 'default', position: 'relative' }}
    >
      {/* Thumbnail with optional quantity badge */}
      <div style={{ position: 'relative' }}>
        <ItemThumbnail
          item={item}
          size={size}
          token={token}
          onClick={handleImageClick}
          showCompletedBadge={false}
          renderPlaceholder={renderPlaceholder}
          isGuestToken={isGuestToken}
        />
        
        {/* Quantity badge on thumbnail (when using swipe mode, not marketplace) */}
        {features.showQuantity && useQuantityBadge && !features.showPrice && (
          <QuantityBadge
            quantity={item.quantity || 0}
            onClick={isSelectMode ? undefined : () => onQuantityBadgeClick?.(item)}
            highlightZero={highlightZeroQuantity}
            position="top-right"
          />
        )}
      </div>
      
      {/* Name (always shown in grid) - clickable to open edit menu */}
      <div 
        className="item-card-name"
        onClick={(e) => {
          if (!isReadOnly && onMenuClick) {
            e.stopPropagation();
            onMenuClick(item);
          }
        }}
        style={{
          color: item.quantity === -1 
            ? 'var(--color-item-name-disabled)' // Dimmed for -1 (don't want to restock)
            : hasZeroQuantity 
              ? 'var(--color-warning)' // Orange for 0 (out of stock)
              : 'var(--color-item-name)', // Theme-aware item name color
          cursor: isReadOnly ? 'default' : 'pointer',
        }}
      >
        {showQuantityInName && item.quantity !== undefined && (
          <span style={{ marginRight: '4px' }}>{item.quantity}</span>
        )}
        {item.name}
      </div>
      
      {/* ID + Condition for marketplace (grid) */}
      {features.showPrice && (
        <div style={{
          padding: '0 8px',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
          textAlign: 'center',
        }}>
          #{item.id}{item.condition && CONDITION_LABELS[item.condition] ? ` • ${CONDITION_LABELS[item.condition]}` : ''}
        </div>
      )}

      {/* Condition for non-marketplace (grid) */}
      {!features.showPrice && item.condition && CONDITION_LABELS[item.condition] && (
        <div style={{
          padding: '0 8px',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
          fontStyle: 'italic',
          textAlign: 'center',
        }}>
          {CONDITION_LABELS[item.condition]}
        </div>
      )}

      {/* Marketplace price + qty info (grid) */}
      {features.showPrice && (
        <div className="item-marketplace-info" style={{ padding: '0 8px 4px', fontSize: 'var(--font-size-xs)', justifyContent: 'center' }}>
          {item.sale_price != null && item.sale_price > 0 && (
            <span className="item-marketplace-price">${centsToDisplay(item.sale_price)} each</span>
          )}
          {item.quantity != null && (
            <span
              className="item-marketplace-qty"
              onClick={onQuantityBadgeClick ? (e) => { e.stopPropagation(); onQuantityBadgeClick(item); } : undefined}
              style={onQuantityBadgeClick ? { cursor: 'pointer' } : undefined}
            >
              Qty: <span className="item-marketplace-qty-value">{item.quantity}</span>
            </span>
          )}
        </div>
      )}
      
      {/* Quantity controls (if stock and not using badge and not in select mode) */}
      {features.showQuantity && !features.showPrice && !useQuantityBadge && !isSelectMode && (
        <QuantityControls
          itemId={item.id}
          quantity={item.quantity || 0}
          onChange={onQuantityChange}
          compact={false}
        />
      )}
      
      {/* Checkbox overlay (if checklist) - upper right (hidden in select mode) */}
      {features.showCheckbox && !isSelectMode && (
        <CheckboxOverlay
          isChecked={item.is_checked || isFadingOut || false}
          onToggle={(e: React.MouseEvent) => onCheckboxToggle?.(item, e)}
          position="right"
          layout="grid"
          isInBindle={isInBindle}
        />
      )}
      
      {/* Lock icon for read-only items */}
      {shouldShowLock && (
        <div
          style={{
            position: 'absolute',
            top: '8px',
            right: features.showCheckbox ? '56px' : '8px',
            opacity: 0.5,
          }}
          aria-label="Read only"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
          </svg>
        </div>
      )}
      
      {/* Custom overlay */}
      {renderOverlay?.(item)}
    </div>
  );
};
